import { getCurrentInstance, onMounted, ref } from 'vue';
import dayjs from 'dayjs';
import { callFantasayAppFromKConfNew, OnelinkSceneKey } from '@/common/call-app';

import device from '~/common/device-info';
import { SafetyLocalStorage } from '~/common/storage';
import { checkIfBomdiaApp, checkIfAiChatApp, checkIfIOSApp, checkIfKwaiProApp } from '~/common/app';
import { parseJsonSatefy } from '@/common/utils';
import { logForFantasyPopupClick } from '../scripts/logger';

interface CallFantasayRecord {
    sendMessageCount: number; // 累计发送消息的次数
    lastCallAppTimestamp?: string; // 上次弹窗 or 拉端的时间戳
    todayPopupCount: number; // 累计弹窗的次数
}

const CallFantasayAppStorageKey = 'call_fantasay_app_record';

export const useCallFantasayApp = () => {
    const { proxy } = getCurrentInstance() as { proxy: Record<string, any> };
    const disableCallFantasayAppEnv = checkIfBomdiaApp() || checkIfAiChatApp() || checkIfIOSApp();
    const isKwaiProAppEnv = checkIfKwaiProApp();

    const {
        popoup_or_no, // true 表示弹窗，false 表示直接拉端
        poppup_n_rounds, // n 轮对话会出现一次弹窗
        popup_upper_limit, // 每天的弹窗次数上限
    } = proxy?.$store?.state?.abTestModule?.abTestParams;

    const installAppPopupVisible = ref(false);
    const callFantasayRecord = ref<CallFantasayRecord>({
        sendMessageCount: 0,
        lastCallAppTimestamp: undefined,
        todayPopupCount: 0,
    });

    function checkAndRefreshPopupRecord() {
        const localMessageData = SafetyLocalStorage.getItem(CallFantasayAppStorageKey);
        if (isKwaiProAppEnv || !localMessageData) {
            return;
        }

        const storageData = parseJsonSatefy<CallFantasayRecord>(localMessageData);

        // 最后一次弹窗是否早于今天
        const isLastPopupBeforeToday = dayjs(Number(storageData?.lastCallAppTimestamp)).isBefore(dayjs(Date.now()), 'day');

        callFantasayRecord.value = {
            ...callFantasayRecord.value,
            lastCallAppTimestamp: storageData?.lastCallAppTimestamp,
            todayPopupCount: isLastPopupBeforeToday ? 0 : storageData?.todayPopupCount || 0,
        };
    }

    const handleCallFantasayApp = (botId: string) => {
        const { isIOS } = device();
        if (isIOS) {
            const storeLink = 'https://apps.apple.com/app/weeeye-novo-ia-chat/id6743759736';
            window.open(storeLink, '_blank');
        } else {
            callFantasayAppFromKConfNew({
                scene: OnelinkSceneKey.WebAiChatMessageSend,
                proxy,
                customsParams: {
                    callAppPath: `botdetail/${botId}`,
                },
            });
        }
    };

    const handleCallFantasayAppConfirm = (botId: string) => {
        installAppPopupVisible.value = false;
        handleCallFantasayApp(botId);
        logForFantasyPopupClick('dialogue', 'go');
    };

    const handleCallFantasayAppCancle = () => {
        installAppPopupVisible.value = false;
        logForFantasyPopupClick('dialogue', 'close');
    };

    const handleMessageSendCountChange = (botId: string) => {
        callFantasayRecord.value = {
            ...callFantasayRecord.value,
            sendMessageCount: callFantasayRecord.value.sendMessageCount + 1,
        };

        checkAndRefreshPopupRecord();

        const hasReachedPopupLimit = callFantasayRecord.value.todayPopupCount >= Number(popup_upper_limit); // 到达弹窗次数上限
        const isPopupTriggerMoment = poppup_n_rounds && callFantasayRecord.value.sendMessageCount % Number(poppup_n_rounds) === 0; // 完成 n 轮对话，应该触发弹窗

        const shouldPopupOnWeb = isPopupTriggerMoment && !hasReachedPopupLimit && !isKwaiProAppEnv;
        const shouldPopupOnKwaiPro = isKwaiProAppEnv && isPopupTriggerMoment; // kwai pro 的弹窗逻辑是，只要完成 n 轮对话，就弹窗，不考虑每日上限

        if (disableCallFantasayAppEnv || (!shouldPopupOnWeb && !shouldPopupOnKwaiPro)) {
            return;
        }

        callFantasayRecord.value = {
            ...callFantasayRecord.value,
            lastCallAppTimestamp: Date.now().toString(),
            todayPopupCount: callFantasayRecord.value.todayPopupCount + 1,
        };

        SafetyLocalStorage.setItem(
            CallFantasayAppStorageKey,
            JSON.stringify({
                lastCallAppTimestamp: callFantasayRecord.value.lastCallAppTimestamp,
                todayPopupCount: callFantasayRecord.value.todayPopupCount,
            })
        );

        if (popoup_or_no) {
            installAppPopupVisible.value = true;
        } else {
            handleCallFantasayApp(botId);
        }
    };

    onMounted(() => {
        checkAndRefreshPopupRecord();
    });

    return {
        installAppPopupVisible,

        handleCallFantasayAppConfirm,
        handleCallFantasayAppCancle,
        handleMessageSendCountChange,
    };
};
